import React, { ElementType } from "react";
import ModalHeader from "./ModalHeader";
import { ModalBody, ModalFooter, ModalTitle } from "./ModalContent";
import { ModalContextProvider } from "./ModalContext";

export type ModalSize = "sm" | "md" | "lg" | "xl";

interface ModalProps {
  show?: any;
  onHide?: any;
  className?: string;
  children: React.ReactNode;
  as?: ElementType;
  id?: string;
  placement?: string;
  dialogClassName?: string;
  size?: ModalSize;
}

/**
 * Desktop width. `max-w-*` rather than `w-*`, so it never fights the `w-full`
 * that makes the mobile sheet span the screen.
 */
const SIZES: Record<ModalSize, string> = {
  sm: "md:max-w-[25rem]",
  md: "md:max-w-[30rem]",
  lg: "md:max-w-[40rem]",
  xl: "md:max-w-[56rem]",
};

/**
 * A centred dialog on desktop, a bottom sheet on a phone.
 *
 * This component used to contribute no layout at all — every caller passed its
 * own `fixed … left-2/4 -translate-x-2/4 -translate-y-2/4` positioning string
 * and a `w-screen md:w-[30rem]` width, identically, 26 times. Two problems came
 * out of that:
 *
 * - `w-screen` is `100vw`, so on a phone the dialog was exactly viewport-wide
 *   with no gutter, its rounded corners clipped off either edge, and on iOS the
 *   scrollbar gutter made the whole page scroll sideways whenever one opened.
 * - There was no `top-2/4` to go with the `-translate-y-2/4`. With `position:
 *   fixed` and auto insets the box sits at its *static* position in the flow
 *   and is then pulled up by half its height, so every modal's vertical
 *   placement depended on where `<Modal>` happened to sit in that page's JSX.
 *   That was a desktop bug too.
 *
 * Owning layout here fixes both everywhere at once. Callers keep `className`
 * and `dialogClassName` for anything genuinely their own; width comes from
 * `size`. Appending corrective classes was not an option — Tailwind resolves
 * `w-screen` against `w-full` by stylesheet order, not by the order they appear
 * in a `className` string.
 */
const Modal = ({
  show,
  onHide,
  children,
  className,
  placement,
  id,
  dialogClassName,
  size = "md",
  as: Component = "div",
  ...props
}: ModalProps) => {
  if (!show) return null;

  return (
    <React.Fragment>
      <div
        {...props}
        id={id ? id : "defaultModal"}
        // `pointer-events-none` so the backdrop underneath still takes the
        // click that closes it; the dialog itself opts back in.
        className={`fixed inset-0 z-drawer flex items-end justify-center md:items-center p-0 md:p-4 pointer-events-none ${
          className ?? ""
        }`}
      >
        <ModalContextProvider show={show} onHide={onHide}>
          <Component
            className={`modal-sheet pointer-events-auto flex flex-col w-full overflow-hidden bg-white dark:bg-zink-600 shadow-xl rounded-t-2xl md:rounded-lg pb-[env(safe-area-inset-bottom)] md:pb-0 ${
              SIZES[size]
            } ${dialogClassName ?? ""}`}
          >
            {/* The grab handle that tells you a sheet can be dismissed. */}
            <div className="mx-auto mt-2 mb-1 h-1 w-10 shrink-0 rounded-full bg-gray-300 md:hidden" />
            {children}
          </Component>
        </ModalContextProvider>
      </div>
      <div
        onClick={onHide}
        className={`fixed inset-0 bg-slate-900/40 dark:bg-zink-800/70 z-[1049] backdrop-overlay ${
          !show ? "hidden" : ""
        }`}
        id="backDropDiv"
      ></div>
    </React.Fragment>
  );
};

export default Object.assign(Modal, {
  Header: ModalHeader,
  Title: ModalTitle,
  Body: ModalBody,
  Footer: ModalFooter,
});
